import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { Modal, Input, Select, Textarea } from '../components/UI'
import { can } from '../components/Sidebar'
import toast from 'react-hot-toast'

const SECTIONS = [
  { id:'products',   icon:'◈', label:'Product Research' },
  { id:'funnel',     icon:'⬡', label:'Funnel Building' },
  { id:'creatives',  icon:'◐', label:'Creatives' },
  { id:'adcopy',     icon:'◻', label:'Ad Copy' },
  { id:'scheduling', icon:'◷', label:'Ad Scheduling' },
  { id:'winning',    icon:'◆', label:'Winning Products' },
]
const SECTION_LABEL = Object.fromEntries(SECTIONS.map(s => [s.id, s.label]))
const SECTION_ICON = Object.fromEntries(SECTIONS.map(s => [s.id, s.icon]))

export default function Resources() {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [expanded, setExpanded] = useState({})

  const load = useCallback(async () => {
    setLoading(true)
    try { setResources(await api.getResources()) }
    catch (err) { toast.error(err.message) }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  function openAdd() { setForm({ section:'funnel', title:'', note:'', link:'' }); setModal('add') }
  function openEdit(r) { setForm({ ...r }); setModal(r) }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  async function save() {
    if (!form.title) { toast.error('Title required'); return }
    setSaving(true)
    try {
      if (modal === 'add') await api.createResource(form)
      else await api.updateResource(modal.id, form)
      toast.success('Saved!'); setModal(null); load()
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }

  async function del(r) {
    if (!window.confirm(`Delete "${r.title}"?`)) return
    try { await api.deleteResource(r.id); toast.success('Deleted'); load() }
    catch (err) { toast.error(err.message) }
  }

  // Group resources by section
  const grouped = {}
  resources.forEach(r => { (grouped[r.section] = grouped[r.section] || []).push(r) })
  // Show sections the user has resources-permission for (owner sees all)
  const perms = (typeof user?.permissions === 'object' && user?.permissions) ? user.permissions : {}
  const sectionsWithResources = SECTIONS.filter(s => isOwner || can(perms, s.id, 'resources', false) || grouped[s.id]?.length)

  if (loading) return (
    <div style={{padding:40,color:'var(--text2)',fontFamily:'DM Mono,monospace',fontSize:13,display:'flex',alignItems:'center',gap:12}}>
      <div style={{width:16,height:16,border:'2px solid var(--border2)',borderTopColor:'var(--accent)',borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
      Loading resources...
    </div>
  )

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">📚 Resources</span>
          <span className="toolbar-sub">{resources.length} items</span>
        </div>
        {isOwner && <button className="btn btn-primary" onClick={openAdd}>+ Add Resource</button>}
      </div>

      <div style={{flex:1,overflowY:'auto',padding:20}}>
        {sectionsWithResources.length === 0 ? (
          <div style={{textAlign:'center',padding:'60px 20px',color:'var(--text3)'}}>
            <div style={{fontSize:36,marginBottom:10}}>📚</div>
            <div style={{fontSize:14,fontWeight:600}}>No resources available</div>
            <div style={{fontSize:12,marginTop:4}}>{isOwner ? 'Click "Add Resource" to create one' : 'No resources have been shared with you yet'}</div>
          </div>
        ) : (
          <div style={{maxWidth:800}}>
            {sectionsWithResources.map(s => {
              const items = grouped[s.id] || []
              if (!items.length && !isOwner) return null
              return (
                <div key={s.id} style={{marginBottom:20}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,paddingBottom:8,borderBottom:'1px solid var(--border)'}}>
                    <span style={{fontFamily:'monospace',fontSize:15}}>{s.icon}</span>
                    <span style={{fontSize:14,fontWeight:700,color:'var(--text)'}}>{s.label}</span>
                    <span style={{fontSize:11,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>{items.length} {items.length===1?'item':'items'}</span>
                  </div>
                  {items.length === 0 ? (
                    <div style={{fontSize:12,color:'var(--text3)',padding:'8px 0'}}>No resources in this section yet</div>
                  ) : (
                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                      {items.map(r => {
                        const isOpen = expanded[r.id]
                        return (
                          <div key={r.id} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
                            <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',cursor: r.note ? 'pointer' : 'default'}}
                              onClick={() => r.note && setExpanded(p => ({...p, [r.id]: !p[r.id]}))}>
                              <div style={{flex:1}}>
                                <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{r.title}</div>
                                {r.note && !isOpen && (
                                  <div style={{fontSize:12,color:'var(--text3)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:500}}>
                                    {r.note.slice(0,90)}{r.note.length>90?'…':''}
                                  </div>
                                )}
                              </div>
                              {r.link && (
                                <a href={r.link} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()}
                                  className="btn btn-secondary btn-sm" style={{textDecoration:'none',whiteSpace:'nowrap'}}>🔗 Open Link</a>
                              )}
                              {r.note && <span style={{fontSize:12,color:'var(--text3)'}}>{isOpen ? '▲' : '▼'}</span>}
                              {isOwner && (
                                <>
                                  <button className="btn btn-secondary btn-sm" onClick={e=>{e.stopPropagation();openEdit(r)}}>Edit</button>
                                  <button className="btn btn-danger btn-sm" onClick={e=>{e.stopPropagation();del(r)}}>Del</button>
                                </>
                              )}
                            </div>
                            {isOpen && r.note && (
                              <div style={{padding:'0 14px 14px 14px',fontSize:13,color:'var(--text2)',lineHeight:1.7,whiteSpace:'pre-wrap',borderTop:'1px solid var(--border)',paddingTop:12}}>
                                {r.note}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {modal && (
        <Modal title={modal==='add'?'Add Resource':'Edit Resource'} onClose={()=>setModal(null)}
          footer={<><button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button></>}>
          <Select label="Section" options={SECTIONS.map(s=>({value:s.id,label:s.label}))} value={form.section||'funnel'} onChange={set('section')} />
          <Input label="Title" value={form.title||''} onChange={set('title')} placeholder="e.g. Funnel Naming Convention" />
          <Textarea label="Note / Instructions (optional)" value={form.note||''} onChange={set('note')} style={{minHeight:160}} placeholder="Write detailed instructions here..." />
          <Input label="Link (optional)" type="url" value={form.link||''} onChange={set('link')} placeholder="https://..." />
          <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>Add a note, a link, or both. Employees with Resources permission for this section will see it.</div>
        </Modal>
      )}
    </div>
  )
}
